'use client';

import { useMemo } from 'react';
import Link from 'next/link';

// swiper
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/autoplay';
import { Autoplay } from 'swiper/modules';

// redux
import { useGetTopRetailersQuery } from '@/redux/home/home.api';
import { TopRetailerData } from '@/redux/home/home.types';

// components
import FallbackImage from '@/components/common/FallbackImage';
import ErrorComponent from '../error';

// analytics
import {
  trackRetailerClick,
  PageType,
  PlacementType
} from '@/lib/posthog/posthog-events';

// constants
import { locale } from '@/constants/locale';
import { Skeleton } from '@/components/ui/skeleton';

const MAX_SLIDES_PER_VIEW = 8;

const { home: homeLocale } = locale;

const SkeletonLoader = () => (
  <section className="mt-6 lg:mt-14" role="status" aria-busy="true">
    <span className="sr-only">{locale.common.loading}</span>
    <div className="mx-auto w-full max-w-400">
      <div className="flex items-center justify-between gap-3 px-4 sm:px-5 md:pl-18 lg:pr-18 xl:px-32.5">
        {/* Matches the real h2: 48px, growing to 64px at sm. */}
        <Skeleton className="h-12 w-32 sm:h-16 sm:w-40 lg:w-48" />
        <Skeleton className="h-3.5 w-16 shrink-0 sm:h-4 sm:w-20" />
      </div>

      {/* The Swiper's 16px gap starts at 520, which sits between `480` and `sm`. */}
      <div className="mt-4 flex gap-3 overflow-hidden pr-0 pl-4 min-[520px]:gap-4 sm:px-5 lg:px-18 xl:gap-4.5 xl:px-32.5">
        {Array.from({ length: 8 }).map((_, index) => (
          <div className="shrink-0 text-center" key={index}>
            <Skeleton className="3xl:size-36 4xl:size-38 mx-auto size-24 min-h-24 min-w-24 rounded-full 2xl:size-32" />
            {/* leading-none wins over text-base here, so the label is 14px then 16px. */}
            <Skeleton className="mx-auto mt-4 h-3.5 w-16 sm:mt-3 sm:w-20 lg:mt-8 lg:h-4 lg:w-24" />
          </div>
        ))}
      </div>
    </div>
  </section>
);

type Props = {
  title?: string;
  retailers?: TopRetailerData[];
  pageType?: PageType;
  // Where the "see all" link points. `null` drops the link entirely.
  seeAllHref?: string | null;
  // Analytics bucket. Defaults to the homepage widget so existing callers
  // report exactly what they always did.
  placementType?: PlacementType;
};

const TopRetailer = ({
  title,
  retailers = [],
  pageType = 'home',
  seeAllHref = '/retailers',
  placementType = 'top_8_widget'
}: Props) => {
  const { data, error, isLoading } = useGetTopRetailersQuery(
    {},
    {
      skip: !!retailers.length
    }
  );

  const topRetailers: TopRetailerData[] = useMemo(
    () => (retailers.length > 0 ? retailers : data?.data || []),
    [data?.data, retailers]
  );
  const canLoop = topRetailers.length > MAX_SLIDES_PER_VIEW + 1;

  if (isLoading) {
    return <SkeletonLoader />;
  }
  if (error) return <ErrorComponent />;
  return (
    <section className="mt-6 lg:mt-14">
      <div className="mx-auto w-full max-w-400">
        <div className="flex items-center justify-between gap-3 px-4 sm:px-5 md:pl-18 lg:pr-18 xl:px-32.5">
          <h2 className="font-manrope text-foreground text-left text-2xl/loose font-extrabold sm:text-[32px]">
            {title || homeLocale.topRetailerTitle}
          </h2>
          {seeAllHref ? (
            <Link
              href={seeAllHref}
              className="font-manrope text-blue-green-ink shrink-0 text-right text-sm leading-none font-bold sm:text-base"
            >
              {homeLocale.topRetailerSeeAll}
            </Link>
          ) : null}
        </div>
        <div className="mt-4 pr-0 pl-4 sm:px-5 lg:px-18 xl:px-32.5">
          <Swiper
            modules={[Autoplay]}
            spaceBetween={12}
            slidesPerView={3.5}
            loop={canLoop}
            rewind={!canLoop}
            observer={false}
            observeParents={false}
            observeSlideChildren={false}
            resizeObserver={false}
            updateOnWindowResize={true}
            breakpoints={{
              460: {
                slidesPerView: 4,
                spaceBetween: 12
              },
              520: {
                slidesPerView: 4.5,
                spaceBetween: 16
              },
              640: {
                slidesPerView: 5,
                spaceBetween: 16
              },
              768: {
                slidesPerView: 5.5
              },
              900: {
                slidesPerView: 7
              },
              1024: {
                slidesPerView: 8
              },
              1280: {
                slidesPerView: 8,
                spaceBetween: 18
              }
            }}
          >
            {topRetailers?.map(
              ({ name, slug, logo }: TopRetailerData, index) => (
                <SwiperSlide key={slug}>
                  <Link
                    href={`/retailer/${slug}`}
                    className="pressable inline-block"
                    onClick={() =>
                      trackRetailerClick({
                        shop_id: slug,
                        placement_type: placementType,
                        placement_position: index + 1,
                        page_type: pageType
                      })
                    }
                  >
                    <div className="text-center">
                      <FallbackImage
                        className="3xl:size-36 4xl:size-38 bg-card size-24 min-h-24 min-w-24 rounded-full object-cover object-center 2xl:size-32"
                        src={logo}
                        alt={name}
                        width={200}
                        height={200}
                        sizes="(max-width: 1536px) 96px, (max-width: 1792px) 128px, (max-width: 2048px) 144px, 152px"
                      />
                      <p className="font-manrope text-foreground mt-4 text-sm leading-none font-bold opacity-70 sm:mt-3 lg:mt-8 lg:text-base">
                        {name}
                      </p>
                    </div>
                  </Link>
                </SwiperSlide>
              )
            )}
          </Swiper>
        </div>
      </div>
    </section>
  );
};

export default TopRetailer;
